import {
  useEffect,
} from 'react'
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import {
  Mail,
} from 'lucide-react'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'

/**
 * Shown when a signup lands past the daily registration cap and joins the
 * waitlist. Everything here is reassurance: the account already exists with its
 * OAuth tokens stored, and the backend drain sweep admits it automatically as
 * capacity reopens — so the one thing this page must not do is imply the user
 * has to come back and try again.
 */
export function PendingPage(): JSX.Element {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const email = searchParams.get('email') || ''
  const name = searchParams.get('name') || ''
  const positionParam = Number(searchParams.get('position'))
  const position = Number.isInteger(positionParam) && positionParam > 0 ? positionParam : null

  useEffect(() => {
    trackEvent(OnboardingEvents.WAITLIST_JOINED_SHOWN, { has_position: position !== null })
  }, [position])

  const handleRetry = (): void => {
    trackEvent(OnboardingEvents.WAITLIST_RETRY_CLICKED)
    navigate('/login')
  }

  const firstName = name.trim().split(' ')[0]

  return (
    <div className="flex justify-center py-4 sm:py-10">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-soft-lg p-6 sm:p-10 text-center">
        <h1 className="text-3xl sm:text-4xl font-display uppercase tracking-tight text-gray-900 mb-3">
          You're on the list
        </h1>
        <p className="text-gray-600 mb-8">
          {firstName ? `${firstName}, we're` : "We're"} opening Catch to a set number of
          people each day so every new account gets a proper welcome — and today's is full.
        </p>

        {position !== null && (
          <div className="rounded-2xl bg-gray-50 border border-gray-100 py-6 mb-8">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
              Your place in line
            </p>
            <p className="text-5xl font-display text-gray-900 mt-1">
              #{position}
            </p>
          </div>
        )}

        <div className="flex gap-3 text-left rounded-2xl bg-primary-50 p-4 mb-8">
          <Mail className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-gray-900">Nothing else to do</p>
            <p className="text-sm text-gray-600">
              Your account is set up and waiting. We'll switch it on and email
              {email ? ' ' : ' you '}
              {email && <strong className="font-medium text-gray-900">{email}</strong>}
              {' '}
              the moment a place opens.
            </p>
          </div>
        </div>

        <button
          onClick={handleRetry}
          className="w-full py-3 px-6 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold shadow-soft hover:shadow-soft-lg transition-[background-color,box-shadow,transform] duration-200 active:scale-[0.96]"
        >
          Check if you're in
        </button>
        <p className="text-sm text-gray-400 mt-3">
          If a place opened, this takes you straight into Catch.
        </p>
      </div>
    </div>
  )
}
